import DialogContentText from '@mui/material/DialogContentText'
import Typography from '@mui/material/Typography'
import { omit, pathOr } from 'ramda'
import React from 'react'

import { Checkbox } from '@/app/component/atom/form'
import { getPartType } from '@/app/module/campaigns/component/item/steps/content/types/multimessage/content'
import { estimateSubscription } from '@/app/module/campaigns/helpers'
import { isReminderCampaign } from '@/app/module/campaigns/models/reminder/transform'
import { type CampaignItem, CampaignTemplateType, type Delta, TimeUnit } from '@/app/module/campaigns/types'
import { toTimezoneOptions } from '@/app/service/util/timezone'
import { type CountryTimezone, type TimezoneWithCountryCodeType } from '@/app/types'

import Delay from './delay'
import ReminderEventTime from './reminder-event-time'
import StartAt from './start-at'

export type DelaySubscriptionPayload = {
  delay?: Omit<Delta, 'timezones' | 'valueError'>
  startAt?: string
  t0?: string
  t0timezone?: string
}

type Props = {
  campaign: CampaignItem
  countriesTimezones: CountryTimezone[]
  countryCode: string
  timezone: string
  timezones: TimezoneWithCountryCodeType
  onChange: (payload: DelaySubscriptionPayload) => void
  setConfirmButtonDisabled?: (disabled: boolean) => void
}

const getDelaySubscriptionPayload = ({
  campaign,
  delay,
  selectedMessage,
  t0,
  t0timezone,
}: {
  campaign: CampaignItem
  delay: Delta
  selectedMessage: string
  t0: string
  t0timezone: string
}): DelaySubscriptionPayload => {
  const newTimezone = t0 ? t0timezone : ''

  if (delay.unit === TimeUnit.Immediate) {
    return {
      startAt: selectedMessage,
      t0,
      t0timezone: newTimezone,
    }
  }

  return {
    delay: omit(['timezones', 'valueError'], delay),
    t0,
    t0timezone: newTimezone,
    ...(campaign.type === CampaignTemplateType.Drip && {
      startAt: selectedMessage,
    }),
  }
}

const DelayDialogContent: React.FC<Props> = ({
  campaign,
  countriesTimezones,
  countryCode,
  timezone,
  timezones,
  onChange,
  setConfirmButtonDisabled,
}) => {
  const [delay, setDelay] = React.useState({
    unit: TimeUnit.Immediate,
    value: 0,
    dayOfWeek: '',
    daysOfWeek: {},
    time: '',
    timezone,
    timezones: toTimezoneOptions(pathOr([], [countryCode, 'tzs'], timezones)),
    country: countryCode,
    at: undefined,
    valueError: '',
  })
  const [isDefaultArgs, setIsDefaultArgs] = React.useState(true)
  const [selectedMessage, selectMessage] = React.useState('')
  const [t0, setT0] = React.useState('')
  const [t0timezone, setT0Timezone] = React.useState(timezone)

  const { message, protip } = estimateSubscription(delay, new Date())

  const isReminder = isReminderCampaign(campaign)
  const hasError = !!delay.valueError || (!isDefaultArgs && !t0)

  const handleChangeReminderArgs = React.useCallback((t0Value: string, tzValue: string) => {
    setT0(t0Value)
    setT0Timezone(tzValue)
  }, [])

  const messages = React.useMemo(() => {
    if (campaign.type !== CampaignTemplateType.Drip || isReminder) {
      return []
    }
    return campaign.variables.parts.map((part, index) => {
      const type = getPartType(part)
      return {
        type,
        id: part.id,
        delta: part.delta,
        index: index + 1,
        label: part.label,
      }
    })
  }, [campaign, isReminder])

  const payload = React.useMemo(
    () => getDelaySubscriptionPayload({ campaign, delay, selectedMessage, t0, t0timezone }),
    [campaign, delay, selectedMessage, t0, t0timezone],
  )

  React.useEffect(() => {
    onChange(payload)
  }, [onChange, payload])

  React.useEffect(() => {
    setConfirmButtonDisabled?.(hasError)
  }, [hasError, setConfirmButtonDisabled])

  return (
    <>
      {campaign.type === CampaignTemplateType.Drip && (
        <>
          <DialogContentText>What message should the contact(s) start at?</DialogContentText>
          <StartAt messages={messages} selectMessage={selectMessage} selectedMessage={selectedMessage} />
        </>
      )}
      <DialogContentText>When should the subscription start?</DialogContentText>
      <Delay
        isFirst={true}
        published={false}
        item={delay}
        changeHandler={setDelay}
        timezones={timezones}
        countriesTimezones={countriesTimezones}
        setLoading={() => {}}
        type="message"
        text=""
      />
      <div>
        <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
          {message}
        </Typography>
      </div>
      <div>
        <Typography variant="caption" color="primary" style={{ fontWeight: 'bold' }}>
          {protip}
        </Typography>
      </div>
      {isReminder && (
        <>
          <Checkbox
            editable
            label="Use the event time as configured in the campaign"
            name="event-time-enabled"
            value={String(isDefaultArgs)}
            onChange={({ value }) => {
              const boolValue = value === 'true'
              setIsDefaultArgs(boolValue)

              if (boolValue) {
                // reset t0 and t0timezone when switching to default
                setT0('')
                setT0Timezone('')
              }
            }}
          />

          {!isDefaultArgs && (
            <ReminderEventTime
              countriesTimezones={countriesTimezones}
              countryCode={countryCode}
              t0={t0}
              t0timezone={t0timezone}
              timezones={timezones}
              onChange={handleChangeReminderArgs}
            />
          )}
        </>
      )}
    </>
  )
}

export default DelayDialogContent
